import { type JSX, type ReactNode } from "react";
import parse, { Element, type HTMLReactParserOptions } from "html-react-parser";

import {
  DYNAMIC_FOOTNOTE_ATTRIBUTE,
  DynamicFootnote,
} from "@vega-core/components/HtmlContent/DynamicFootnote";
import {
  DYNAMIC_TOOLTIP_ATTRIBUTE,
  DynamicTooltip,
} from "@vega-core/components/HtmlContent/DynamicTooltip";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = {
  content?: ReactNode | string;
  applyBaseTheme?: boolean;
  hideFootnotes?: boolean;
  options?: HTMLReactParserOptions;
};

const normalizeContent = (content: Props["content"]) => {
  if (typeof content !== "string") {
    return content;
  }

  const doc = new DOMParser().parseFromString(content, "text/html");
  const styles = Array.from(doc.querySelectorAll("style"))
    .map((el) => el.outerHTML)
    .join("\n");
  const body = doc.body.innerHTML;

  if (!body) return content;

  return styles ? styles + body : body;
};

export function HtmlContent({
  content,
  applyBaseTheme,
  hideFootnotes,
  options,
}: Props) {
  const normalizedContent = normalizeContent(content);

  return isTruthy(normalizedContent) ? (
    <DynamicContentRenderer
      applyBaseTheme={applyBaseTheme}
      dynamicString={normalizedContent}
      hideFootnotes={hideFootnotes}
      key={normalizedContent.toString()}
      options={options}
    />
  ) : null;
}

type DProps = {
  dynamicString: JSX.Element | ReactNode | string;
  hideFootnotes?: boolean;
  options?: HTMLReactParserOptions;
  applyBaseTheme?: boolean;
};

export function DynamicContentRenderer({
  dynamicString,
  hideFootnotes,
  options,
  applyBaseTheme,
}: DProps) {
  const parserOptions: HTMLReactParserOptions = {
    replace(domNode) {
      if (domNode instanceof Element) {
        if (isTruthy(domNode.attribs[DYNAMIC_TOOLTIP_ATTRIBUTE])) {
          return <DynamicTooltip domNode={domNode} />;
        }

        if (isTruthy(domNode.attribs[DYNAMIC_FOOTNOTE_ATTRIBUTE])) {
          return !hideFootnotes ? (
            <DynamicFootnote domNode={domNode} options={parserOptions} />
          ) : (
            <span />
          );
        }

        if (applyBaseTheme && domNode.name === "img") {
          return <span className="hidden" />;
        }

        if (applyBaseTheme && domNode.attribs.style) {
          const style = domNode.attribs.style;

          if (style) {
            let newStyle = style.replace(/color:\s*[^;]+;?/g, "");
            newStyle = newStyle.replace(/letter-spacing:\s*[^;]+;?/g, "");

            if (newStyle.trim()) {
              domNode.attribs.style = newStyle;
            } else {
              delete domNode.attribs.style;
            }
          }
        }
      }
    },
  };

  const parsedElement =
    typeof dynamicString === "string"
      ? parse(dynamicString, options ?? parserOptions)
      : dynamicString;

  return (
    <span
      className={cnTwMerge("htmlContent", {
        applyBaseTheme: applyBaseTheme,
      })}
    >
      {parsedElement}
    </span>
  );
}
